import React, {
  PropsWithChildren,
  useEffect,
  useState,
  DragEvent,
  useRef,
  useContext
} from 'react';
import FolderItem from './FolderItem';
import { Box, Breadcrumbs, Grid, Link } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { push } from 'react-router-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  DragAndDropContextActions,
  DragAndDropStore
} from '../context/DragAndDrop.context';
import colors from '../../../../../styles/colors';

export interface FolderListProps {
  folders: any[];
  paths: any[];
  handleCreateFolder(name: string): any;
  handleFolderEdit(folderId: number, name: string): any;
  handleFolderItemMove(moveItem: any): any;
  handleFolderDelete(folderId: number): any;
  updateFolderList(folders: any[]): any;
}

const useStyles = makeStyles({
  dragging: {
    border: '2px solid black',
    opacity: 1,
    borderRadius: '4px'
  },
  highlightedCrumb: {
    textDecoration: 'underline',
    color: colors.blue,
    fontWeight: 'bold'
  },
  highlightedFolder: {
    opacity: 0.4
  },
  highlightedLatestFolderItem: {
    color: colors.blue
  }
});

const FolderList: React.FC<FolderListProps> = (
  props: PropsWithChildren<FolderListProps>
) => {
  const classes = useStyles();
  const {
    folders,
    paths,
    handleCreateFolder,
    updateFolderList,
    handleFolderEdit,
    handleFolderDelete,
    handleFolderItemMove
  } = props;
  const dispatch = useDispatch();
  const { state: dragAndDropState, dispatch: dragAndDropDispatch } = useContext(
    DragAndDropStore
  );
  const { dragItem, dragNode, highlightBreadCrumb } = dragAndDropState;
  const history = useHistory();
  const [foldersData, setFoldersData] = useState(folders);
  const [isFolderDragging, setIsFolderDragging] = useState(false);
  useEffect(() => {
    setFoldersData(folders);
  }, [folders]);

  const removeFolderItem = () => {
    const newFolders = foldersData.filter(folder => folder.id);
    updateFolderList(newFolders);
  };
  // @TODO need to work on breadcrumb structure and data fetching
  const handleFolderCreation = (name: string, folderId?: number) => {
    if (folderId) {
      handleFolderEdit(folderId, name);
    } else {
      handleCreateFolder(name);
    }
  };
  const handleFolderSetEditMode = (folderId: number) => {
    const newFolders = foldersData.map((folder: any) => {
      return {
        ...folder,
        isEditMode: folder.id === folderId ? !folder.isEditMode : false
      };
    });
    setFoldersData([...newFolders]);
  };

  const goIntoFolderView = (folderId?: number) => {
    if (!folderId) {
      return dispatch(push(`${history.location.pathname}`));
    }
    dispatch(push(`${history.location.pathname}?folderId=${folderId}`));
  };

  /**
   * @description function fired when a folder item is dragged over breadcrumb
   * @param evt
   * @param data
   */
  const onFolderDragEnter = (
    evt: DragEvent<HTMLElement>,
    data: { id: number | null }
  ) => {
    if (evt.target !== dragNode?.current && dragItem?.current?.id !== data.id) {
      dragAndDropDispatch({
        type: DragAndDropContextActions.SET_ACTIVE_BREADCRUMB,
        payload: { id: data.id }
      });
    } else {
      dragAndDropDispatch({
        type: DragAndDropContextActions.SET_ACTIVE_BREADCRUMB,
        payload: { id: null }
      });
    }
  };

  /**
   * @description function fired when a folder starts dragging
   * @param evt
   * @param data
   */
  const onFolderDragStart = (evt: DragEvent<HTMLDivElement>, data: any) => {
    dragItem.current = data;
    dragNode.current = evt.target;
    dragNode.current.addEventListener('dragend', onFolderDragEnd);

    dragAndDropDispatch({
      type: DragAndDropContextActions.SET_DRAG_ITEMS,
      payload: {
        dragItem,
        dragNode
      }
    });
    setTimeout(() => setIsFolderDragging(true));
  };

  /**
   * @description function fired when a folder stops dragging
   * @param evt
   */
  const onFolderDragEnd = (evt?: React.DragEvent<HTMLDivElement>) => {
    dragItem.current = null;
    dragNode.current.removeEventListener('dragend', onFolderDragEnd);
    dragNode.current = null;
    dragAndDropDispatch({
      type: DragAndDropContextActions.SET_DRAG_ITEMS,
      payload: {
        dragItem,
        dragNode,
        highlightBreadCrumb: { id: null }
      }
    });
    setIsFolderDragging(false);
  };

  /**
   * @description fires when a draggable item drops
   * @param evt
   * @param data
   */
  const onDropFolderItem = (evt: React.DragEvent<any>, data: any) => {
    const currentItem = dragItem?.current;
    if (currentItem) {
      const moveData = {
        id: currentItem.id,
        parentId: data.id
      };
      if (moveData.id !== moveData.parentId) {
        handleFolderItemMove(moveData);
      }
    }
  };

  return (
    <>
      <Breadcrumbs style={{ marginBottom: '10px' }} aria-label="breadcrumb">
        <Link
          style={{ cursor: 'pointer' }}
          color="inherit"
          onDragOver={(event: {
            stopPropagation: () => void;
            preventDefault: () => void;
          }) => {
            event.stopPropagation();
            event.preventDefault();
          }}
          onDrop={(evt: React.DragEvent<any>) =>
            onDropFolderItem(evt, { id: 0 })
          }
          onDragEnter={(evt: React.DragEvent<HTMLElement>) =>
            onFolderDragEnter(evt, { id: 0 })
          }
          className={
            highlightBreadCrumb.id === 0 ? classes.highlightedCrumb : undefined
          }
          tabIndex={0}
          onClick={() => goIntoFolderView()}
        >
          / Assignments
        </Link>
        {paths.map((path, index: number) => {
          const isLastOne = paths.length - 1 === index;
          return (
            <Link
              key={path.id}
              style={{ cursor: 'pointer' }}
              className={
                highlightBreadCrumb.id === path.id
                  ? classes.highlightedCrumb
                  : isLastOne
                  ? classes.highlightedLatestFolderItem
                  : undefined
              }
              color="inherit"
              tabIndex={0}
              onDragOver={(event: {
                stopPropagation: () => void;
                preventDefault: () => void;
              }) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              onDrop={(evt: React.DragEvent<any>) =>
                onDropFolderItem(evt, path)
              }
              onDragEnter={(evt: React.DragEvent<HTMLElement>) =>
                onFolderDragEnter(evt, path)
              }
              onClick={() => goIntoFolderView(path.id)}
            >
              {path.name}
            </Link>
          );
        })}
      </Breadcrumbs>
      <Grid container spacing={2}>
        {foldersData.map((folder: any, index: number) => {
          const isLastOne = foldersData.length - 1 === index;
          const isCurrentlyDragging =
            isFolderDragging && dragItem?.current?.id === folder.id;
          return (
            <Grid
              key={folder.id}
              draggable={true}
              onDragStart={(evt: React.DragEvent<HTMLDivElement>) =>
                onFolderDragStart(evt, folder)
              }
              onDragOver={(event: {
                stopPropagation: () => void;
                preventDefault: () => void;
              }) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              onDrop={(evt: React.DragEvent<any>) =>
                onDropFolderItem(evt, folder)
              }
              onDragEnter={(evt: React.DragEvent<HTMLElement>) =>
                onFolderDragEnter(evt, folder)
              }
              onClick={() => !folder.isEditMode && goIntoFolderView(folder.id)}
              className={
                highlightBreadCrumb.id === folder.id
                  ? classes.highlightedFolder
                  : undefined
              }
              item
              xs={12}
              sm={6}
              md={3}
              xl={2}
            >
              <Box
                className={isCurrentlyDragging ? classes.dragging : undefined}
              >
                <FolderItem
                  handleFolderSetEditMode={handleFolderSetEditMode}
                  handleFolderCreation={handleFolderCreation}
                  handleFolderDelete={handleFolderDelete}
                  removeFolderItem={removeFolderItem}
                  folder={folder}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default FolderList;
